<template>
  <div class="lesson-divider-box">
    <VLessonPlaceholder v-if="isEmptyBlock" />
    
    <div v-else
       :style="[
         CssStyleHelpers.margins(fields.mainGroup?.margins),
       ]">
    <div class="divider-container relative"
         :style="[
           CssStyleHelpers.paddings(fields.mainGroup?.paddings),
         ]">
      <div class="divider-line"
           :style="[
             getDividerLineStyle(),
             CssStyleHelpers.borderRadius(fields.dividerGroup?.borderRadius),
           ]">
      </div>
    </div>
  </div>
  </div>
</template>

<script setup lang="ts">
import {computed, PropType} from "vue";
import {useDataFields} from "../composables/data";
import VLessonPlaceholder from "../VLessonPlaceholder.vue";
import {ILayoutComponentData} from "../types/ILayoutFieldData";
import {CssStyleHelpers} from "../utils/cssStyleHelpers";
const props = defineProps({
  modelValue: {
    type: Object as PropType<ILayoutComponentData | undefined>,
    required: true,
  },
  mode: {
    type: String as PropType<"view" | "edit">,
    default: "view",
  },
});

// Використовуємо дані напряму з newData
const applyDefaultMainPaddings = (paddings?: Record<string, number>) => ({
  top: paddings?.top ?? 0,
  right: paddings?.right ?? 0,
  bottom: paddings?.bottom ?? 0,
  left: paddings?.left ?? 0,
  ...paddings,
});

const applyDefaultMainMargins = (margins?: Record<string, number>) => ({
  top: margins?.top ?? 50,
  right: margins?.right ?? 0,
  bottom: margins?.bottom ?? 0,
  left: margins?.left ?? 0,
  ...margins,
});

const fields = computed(() => {
  const newData = props.modelValue?.data;
  if (!newData)
    return {
      dividerGroup: {},
      mainGroup: {},
    };

  const {getData} = useDataFields(newData);

  return {
    dividerGroup: {
      borderRadius: getData("dividerGroup.borderRadius")?.value,
      border: getData("dividerGroup.border"),
    },
    mainGroup: {
      paddings: applyDefaultMainPaddings(getData("mainGroup.paddings")),
      margins: applyDefaultMainMargins(getData("mainGroup.margins")),
    },
  };
});

const isEditMode = computed(() => props.mode === "edit");

// Перевіряємо чи блок пустий
const isEmptyBlock = computed(() => {
  const hasBorder = fields.value?.dividerGroup?.border?.enabled;
  return !hasBorder;
});

// Функція для отримання стилю лінії
const getDividerLineStyle = () => {
  const border = fields.value?.dividerGroup?.border;
  
  // Якщо бордер увімкнено - використовуємо його налаштування
  if (border?.enabled) {
    const borderStyle = border.style || 'solid';
    const borderColor = border.color || '#000000';
    const borderWidth = border.width || 1;
    
    return {
      height: '0',
      borderTop: `${borderWidth}px ${borderStyle} ${borderColor}`,
      borderLeft: 'none',
      borderRight: 'none',
      borderBottom: 'none',
      backgroundColor: 'transparent',
    };
  }
  
  // Коли бордер вимкнено - не показуємо лінію
  return {
    height: '0',
    border: 'none',
    backgroundColor: 'transparent',
  };
};
</script>

<style scoped>
@reference "tailwindcss";
/* Основні стилі для контейнера */
.lesson-divider-box {
  @apply w-full flex flex-col gap-0;
  container-type: inline-size;
}

/* Контейнер для лінії */
.divider-container {
  @apply w-full flex items-center;
}

/* Лінія дівайдера */
.divider-line {
  @apply w-full;
  transition: all 0.3s ease;
}

/* Адаптивність для мобільних пристроїв */
@container (max-width: 768px) {
  .divider-line {
    @apply w-full;
  }
}
</style>
<style>
@import "../styles/dialogs-styles.css";
</style>

